<script>
    import { debounce } from 'lodash'
    import { call, get, sync } from 'vuex-pathify'

    export default {
        data() {
            return {
                errors: null,
                filteredDatasets: [],
            }
        },

        computed: {
            client: get('clients/client'),
            chartTypes: get('chartTypes/chartTypes'),
            tags: sync('tags/tags'),

            canAddMetric() {
                if (!this.chart.type) {
                    return false
                }

                if (this.hasEmptyMetricFields) {
                    return false
                }

                return this.hasMetricOptions && this.hasUnassignedMetrics && this.hasMaxMetric
            },

            categoryTags() {
                return this.tags.filter(tag => tag.type === 'category')
            },

            channelTags() {
                return this.tags.filter(tag => tag.type === 'channel')
            },

            filteredChartTypes() {
                if (this.isSplitColumnBy) {
                    return this.chartTypes.filter(type => type.max_metrics > 1)
                }

                return this.chartTypes
            },

            hasChartType() {
                return this.chart.type
            },

            hasCompatibleMetrics() {
                if (!this.hasChartType) {
                    return true
                }

                return this.hasMetrics
            },

            hasData() {
                if (!this.chart.datasets) {
                    return false
                }

                return this.chart.datasets.length > 0
            },

            hasEmptyMetricFields() {
                return this.chart.metrics.filter(metric => !metric.field).length > 0
            },

            hasMaxMetric() {
                if (!this.hasChartType) {
                    return false
                }

                return this.chart.type.max_metrics > this.chart.metrics.length
            },

            hasMetricOptions() {
                return this.metricOptions.length > 0
            },

            hasMetrics() {
                return this.metrics.length > 0
            },

            hasUnassignedMetrics() {
                return this.metrics.length > this.chart.metrics.length
            },

            isNumberRounded() {
                return this.chart.number_format === 'integer' ? true : false
            },

            isSingleDimensionChartType() {
                return this.chart.type && this.chart.type.dimensions.length === 1 || false
            },

            isSplitColumnBy() {
                if (this.metrics.filter(metric => metric.split_column_by).length > 0) {
                    return true
                }

                return false
            },

            metricOptions() {
                let metrics = this.metrics

                for (const chartMetric of this.chart.metrics) {
                    if (chartMetric.field) {
                        metrics = metrics.filter(dataset => {
                            if (this.isSplitColumnBy) {
                                return dataset.label !== chartMetric.label
                            }

                            return dataset.field !== chartMetric.field
                        })
                    }
                }

                if (!metrics || metrics.length === 0) {
                    return []
                }

                return metrics
            },

            metrics() {
                return this.filteredDatasets
            },
        },

        methods: {
            loadChartMetrics: call('charts/loadChartMetrics'),
            loadChartTypes: call('chartTypes/loadChartTypes'),

            addTag(type, newTag) {
                const tag = {
                    name: newTag,
                    type: type,
                }

                this.tags.push(tag)
                this.chart.tags[type].push(tag)
            },

            addCategoryTag(newTag) {
                this.addTag('category', newTag)
            },

            addChannelTag(newTag) {
                this.addTag('channel', newTag)
            },

            addEmptyMetric() {
                this.chart.metrics.push({
                    data_category: null,
                    data_format: null,
                    data_type: null,
                    field: null,
                    label: null
                })
            },

            addSplitColumnByMetric() {
                this.chart.metrics.push({
                    data_category: this.metrics[0].data_category,
                    data_format: this.metrics[0].data_format,
                    data_type: this.metrics[0].data_type,
                    field: this.metrics[0].field,
                    label: null,
                    split_column_by: this.metrics[0].split_column_by,
                })
            },

            buildDatasets() {
                let datasets = []

                /*if (this.hasChartTypes(['bar', 'bar-stacked', 'doughnut', 'funnel', 'line', 'line-stacked', 'pie'])) {
                    datasets = [...datasets, ...this.filterDatasets(this.chart.datasets, ['currency', 'number'])]
                }

                if (this.hasChartTypes(['gauge'])) {
                    datasets = [...datasets, ...this.filterDatasets(this.chart.datasets, ['number.percentage', 'number.ratio'])]
                }*/

                this.filteredDatasets = this.chart.datasets
            },

            clearChartType() {
                this.chart.type = null

                this.clearDimension()
                this.clearMetrics()
            },

            clearDimension() {
                this.chart.dimension = null
            },

            clearMetrics() {
                this.chart.metrics = []
            },

            filterDatasets(datasets, categories) {
                let result = []

                datasets.forEach((dataset, index) => {
                    let match = categories.some(category => {
                        let dataInfo = category.split('.')

                        if (dataInfo.length === 2) {
                            return dataInfo[0] === dataset.data_category && dataInfo[1] === dataset.data_format
                        }

                        return dataInfo[0] === dataset.data_category
                    })

                    if (match) {
                        result.push(dataset)
                    }
                })

                return result
            },

            hasChartTypes(types) {
                if (!this.chart.type) {
                    return false
                }

                return types.some(type => type === this.chart.type.slug)
            },

            onCancel() {
                this.close()
                this.$emit('cancel')
            },

            onChartType() {
                this.clearDimension()
                this.clearMetrics()

                if (!this.chart.type) {
                    return {datasets: []}
                }

                if (this.isSingleDimensionChartType) {
                    this.chart.dimension = this.chart.type.dimensions[0]
                }

                if (this.isSplitColumnBy) {
                    this.addSplitColumnByMetric()

                    return
                }

                if (this.chart.metrics.length === 0) {
                    this.addEmptyMetric()
                }
            },

            onDataSourceInput: debounce(async function () {
                this.errors = null
                this.chart.data_source = this.chart.data_source.trim()
                this.$v.chart.data_source.$touch()

                if (!this.$v.chart.data_source.$invalid) {
                    let data = await this.loadChartMetrics(this.chart)

                    if (data.notes.status === 'error') {
                        this.errors = [[data.notes.error]]
                    }

                    this.clearChartType()

                    this.chart.datasets = data.datasets
                    this.chart.dimensions = data.dimensions

                    this.buildDatasets()
                }
            }, 150),

            onMetricDelete(index) {
                this.chart.metrics.splice(index, 1)
            },

            onMetricSelect(selected) {
                let metric = this.metrics.find((m) => m.field === selected.field)
                let chartMetric = this.chart.metrics.find((m) => m.field === selected.field)
                let label = metric.label

                if (chartMetric) {
                    chartMetric.data_category = metric.data_category
                    chartMetric.data_format = metric.data_format
                    chartMetric.data_type = metric.data_type
                    chartMetric.label = label
                }
            },

            setNumberRounding(value) {
                if (value) {
                    this.chart.number_format = 'integer'
                } else {
                    this.chart.number_format = 'float'
                }
            },

            setInverted(value) {
                if (value) {
                    this.chart.is_inverted = true
                } else {
                    this.chart.is_inverted = false
                }
            },
        },
    }
</script>